import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Rocket, Code, Smartphone, BarChart3, Users, Timer, Check, Brain, Zap } from 'lucide-react';
import CtaSection from '../components/CtaSection';
import TestimonialsSection from '../components/TestimonialsSection';
import StatsSection from '../components/StatsSection';
import Process from '../components/Process';

const MvpServicesPage: React.FC = () => {
  // Sample stats data
  const stats = [
    {
      value: '4-8',
      label: 'Weeks Average Development Time'
    },
    {
      value: '50+',
      label: 'Startups Launched'
    },
    {
      value: '92%',
      label: 'Success Rate'
    },
    {
      value: '30%',
      label: 'Cost Savings vs. Traditional Development'
    }
  ];

  // Sample testimonials data
  const testimonials = [
    {
      content: "They helped us turn our idea into a functional MVP in just 6 weeks. The team's startup-focused approach meant they understood exactly what we needed to validate our concept with investors.",
      author: 'Raaj Singh',
      position: 'Founder',
      company: 'Vitran'
    },
    {
      content: "Working with this team was a game-changer for our fintech startup. They delivered an investor-ready MVP ahead of schedule, which helped us secure our seed funding round.",
      author: 'Subodh Diwan',
      position: 'CEO',
      company: 'Neopay'
    },
    {
      content: "Their lean, no-fluff approach to MVP development was exactly what we needed. We launched quickly, collected valuable user feedback, and iterated—all within our tight budget constraints.",
      author: 'David Rodriguez',
      position: 'Co-founder',
      company: 'Fashion Vista'
    }
  ];

  return (
    <>
      {/* What We Do */}
      <section className="py-16 md:py-24 bg-gradient-to-r from-blue-900 to-indigo-600">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block px-3 py-1 rounded-full bg-blue-100 text-blue-600 text-sm font-medium mb-4">
              Our MVP expertise
            </span>
            <h2 className="text-2xl md:text-4xl font-bold mb-4 text-white">
              Rapid MVP Development for Startups
            </h2>
            <p className="md:text-lg text-white">
              You're building something bold. You need a tech partner who gets it. We specialize in turning innovative ideas into working products—fast.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition duration-300">
              <div className="w-14 h-14 mb-6 rounded-lg flex items-center justify-center bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-600">
                <Rocket size={28} />
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-800">MVP Strategy & Roadmapping</h3>
              <p className="text-gray-600 mb-6">
                We define the core features needed to validate your concept while planning for future growth. Clear priorities, defined milestones.
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition duration-300">
              <div className="w-14 h-14 mb-6 rounded-lg flex items-center justify-center bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-600">
                <Smartphone size={28} />
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-800">UX/UI Design</h3>
              <p className="text-gray-600 mb-6">
                User-focused designs that balance aesthetics with functionality. We create interfaces that are intuitive, engaging, and conversion-oriented.
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition duration-300">
              <div className="w-14 h-14 mb-6 rounded-lg flex items-center justify-center bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-600">
                <Code size={28} />
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-800">Full-Stack Development</h3>
              <p className="text-gray-600 mb-6">
                From front-end interfaces to back-end architecture, we build complete solutions for web and mobile platforms using modern technologies.
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition duration-300">
              <div className="w-14 h-14 mb-6 rounded-lg flex items-center justify-center bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-600">
                <Zap size={28} />
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-800">Quick Prototyping & Iteration</h3>
              <p className="text-gray-600 mb-6">
                Rapid prototyping allows you to visualize and test concepts early. We iterate quickly based on feedback for faster time-to-market.
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition duration-300">
              <div className="w-14 h-14 mb-6 rounded-lg flex items-center justify-center bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-600">
                <Users size={28} />
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-800">Deployment & User Feedback</h3>
              <p className="text-gray-600 mb-6">
                We help you launch and implement effective feedback loops to collect valuable user insights that drive product improvements.
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition duration-300">
              <div className="w-14 h-14 mb-6 rounded-lg flex items-center justify-center bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-600">
                <BarChart3 size={28} />
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-800">Investor-Ready Outputs</h3>
              <p className="text-gray-600 mb-6">
                We create demos, decks, and proof-of-concept materials that speak your investors' language and strengthen your fundraising efforts.
              </p>
            </div>
          </div>

          <div className="text-center mt-12">
            <p className="text-lg text-white mb-6">
              From first wireframe to functional product — we move fast, without compromising quality.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center px-6 py-3 border border-gray-300 rounded-lg text-blue-600 bg-white hover:bg-gray-50 font-medium transition duration-300"
            >
              Discuss Your MVP
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      <StatsSection
        stats={stats}
        sectionStyle="dark"
      />

      {/* Why Startups Choose Us */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block px-3 py-1 rounded-full bg-blue-100 text-blue-600 text-sm font-medium mb-4">
              Why Startups Choose Us
            </span>
            <h2 className="text-2xl md:text-4xl font-bold mb-4 text-gray-900">
              Speed. Focus. Clarity.
            </h2>
            <p className="md:text-lg text-gray-600">
              We understand the unique challenges of early-stage startups and focus on what truly matters for your success.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-gray-50 rounded-xl p-8">
              <div className="flex items-start">
                <div className="flex-shrink-0 mr-4">
                  <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center text-white">
                    <Timer size={24} />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-4 text-gray-900">Launch in Weeks, Not Months</h3>
                  <p className="text-gray-600 mb-4">
                    Get to market fast and start validating with real users. Our streamlined process eliminates unnecessary delays and focuses on building what matters most.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <Check className="mr-2 h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">Agile development sprints</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="mr-2 h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">Rapid prototyping and iteration</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="mr-2 h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">Weekly progress reviews</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <div className="flex items-start">
                <div className="flex-shrink-0 mr-4">
                  <div className="w-12 h-12 bg-indigo-600 rounded-full flex items-center justify-center text-white">
                    <Brain size={24} />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-4 text-gray-900">Startup-Savvy Team</h3>
                  <p className="text-gray-600 mb-4">
                    We've worked with dozens of early-stage startups — we know what matters (and what doesn't). Our experience translates into more value for your business.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <Check className="mr-2 h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">Expertise across multiple industries</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="mr-2 h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">Founder-friendly approach</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="mr-2 h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">Scaling and fundraising insights</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <div className="flex items-start">
                <div className="flex-shrink-0 mr-4">
                  <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center text-white">
                    <Zap size={24} />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-4 text-gray-900">Lean, No-Fluff Approach</h3>
                  <p className="text-gray-600 mb-4">
                    We focus on must-haves, not nice-to-haves. Our lean methodology ensures your budget is spent on features that drive validation and user adoption.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <Check className="mr-2 h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">MVP feature prioritization</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="mr-2 h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">Resource optimization</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="mr-2 h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">Value-driven development</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <div className="flex items-start">
                <div className="flex-shrink-0 mr-4">
                  <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center text-white">
                    <BarChart3 size={24} />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-4 text-gray-900">Investor-Ready Outputs</h3>
                  <p className="text-gray-600 mb-4">
                    Demos, decks, and proof-of-concept that speak your investors' language. We help you create assets that strengthen your fundraising efforts.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <Check className="mr-2 h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">Polished demos and presentations</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="mr-2 h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">Metrics-driven case materials</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="mr-2 h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">Technical documentation for due diligence</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Process />

      <TestimonialsSection testimonials={testimonials} />

      {/* FAQs */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block px-3 py-1 rounded-full bg-blue-100 text-blue-600 text-sm font-medium mb-4">
              Frequently Asked Questions
            </span>
            <h2 className="text-2xl md:text-4xl font-bold mb-4 text-gray-900">
              Common Questions About MVP Development
            </h2>
            <p className="md:text-lg text-gray-600">
              Find answers to questions startup founders often ask about our MVP development process.
            </p>
          </div>

          <div className="max-w-4xl mx-auto space-y-6">
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <details className="group">
                <summary className="flex items-center justify-between p-6 cursor-pointer bg-white hover:bg-gray-50 transition duration-300">
                  <h3 className="md:text-lg font-semibold text-gray-900">
                    How long does an MVP take to build?
                  </h3>
                  <span className="ml-6 flex-shrink-0 text-gray-400 group-open:rotate-180 transition duration-300">
                    <svg width="24" height="24" fill="none" viewBox="0 0 24 24">
                      <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                    </svg>
                  </span>
                </summary>
                <div className="px-6 pb-6 text-gray-600">
                  <p>
                    Typically 4–8 weeks depending on complexity. We focus on building the core features that will validate your concept and provide value to early users. Our agile approach allows us to move quickly while maintaining quality.
                  </p>
                </div>
              </details>
            </div>

            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <details className="group">
                <summary className="flex items-center justify-between p-6 cursor-pointer bg-white hover:bg-gray-50 transition duration-300">
                  <h3 className="md:text-lg font-semibold text-gray-900">
                    What technologies do you use?
                  </h3>
                  <span className="ml-6 flex-shrink-0 text-gray-400 group-open:rotate-180 transition duration-300">
                    <svg width="24" height="24" fill="none" viewBox="0 0 24 24">
                      <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                    </svg>
                  </span>
                </summary>
                <div className="px-6 pb-6 text-gray-600">
                  <p>
                    React, Node.js, Django, Flutter, Firebase, AWS — we choose what best fits your use case. Our technology decisions are based on your specific requirements, scalability needs, and long-term goals, not a one-size-fits-all approach.
                  </p>
                </div>
              </details>
            </div>

            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <details className="group">
                <summary className="flex items-center justify-between p-6 cursor-pointer bg-white hover:bg-gray-50 transition duration-300">
                  <h3 className="md:text-lg font-semibold text-gray-900">
                    Can you help after launch?
                  </h3>
                  <span className="ml-6 flex-shrink-0 text-gray-400 group-open:rotate-180 transition duration-300">
                    <svg width="24" height="24" fill="none" viewBox="0 0 24 24">
                      <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                    </svg>
                  </span>
                </summary>
                <div className="px-6 pb-6 text-gray-600">
                  <p>
                    Yes! We offer post-launch iterations, scaling support, and even help with pitching. Many of our clients continue working with us beyond the initial MVP to refine their product based on user feedback, add new features, and scale the technology as they grow.
                  </p>
                </div>
              </details>
            </div>

            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <details className="group">
                <summary className="flex items-center justify-between p-6 cursor-pointer bg-white hover:bg-gray-50 transition duration-300">
                  <h3 className="md:text-lg font-semibold text-gray-900">
                    How much does an MVP cost?
                  </h3>
                  <span className="ml-6 flex-shrink-0 text-gray-400 group-open:rotate-180 transition duration-300">
                    <svg width="24" height="24" fill="none" viewBox="0 0 24 24">
                      <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                    </svg>
                  </span>
                </summary>
                <div className="px-6 pb-6 text-gray-600">
                  <p>
                    MVP costs vary based on complexity and features. We offer flexible pricing models including fixed-price packages. During our initial consultation, we'll discuss your specific needs and provide transparent pricing options.
                  </p>
                </div>
              </details>
            </div>

            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <details className="group">
                <summary className="flex items-center justify-between p-6 cursor-pointer bg-white hover:bg-gray-50 transition duration-300">
                  <h3 className="md:text-lg font-semibold text-gray-900">
                    Do you provide CTO-as-a-Service?
                  </h3>
                  <span className="ml-6 flex-shrink-0 text-gray-400 group-open:rotate-180 transition duration-300">
                    <svg width="24" height="24" fill="none" viewBox="0 0 24 24">
                      <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                    </svg>
                  </span>
                </summary>
                <div className="px-6 pb-6 text-gray-600">
                  <p>
                    Yes, we offer CTO-as-a-Service for early-stage startups. This includes technology strategy, architecture planning, vendor selection, technical due diligence for fundraising, and ongoing technical guidance—all without the cost of a full-time executive.
                  </p>
                </div>
              </details>
            </div>
          </div>
        </div>
      </section>

      <CtaSection
        title="Ready to test your idea in the real world?"
        subtitle="Let's talk. Book a free 30-minute consultation with our MVP experts."
        buttonText="Schedule Your Call"
        buttonLink="/contact"
        sectionStyle="gradient"
      />
    </>
  );
};

export default MvpServicesPage;
